/* ============================================================
   HUIT ARCHIV motion layer
   GSAP + ScrollTrigger, self-hosted, loaded off the critical
   render path.

   Discipline (non-negotiable):
   - transforms + opacity only, never layout properties
   - full prefers-reduced-motion static fallback (everything visible)
   - lighter motion on mobile, no scroll-jacking
   - nothing here blocks LCP or add-to-cart
   - if GSAP fails to load, the page is already fully visible (CSS
     gives the no-js / failed-load state, JS only adds the motion)
   - survives Shopify theme-editor section re-renders: rebuilds reveal
     setup on shopify:section:load, reverts cleanly on unload, never
     leaks ScrollTriggers, never leaves a section blank in the editor

   GSAP is pinned to 3.13.0 and served from theme/assets via asset_url.
   The script tag (snippets/scripts.liquid) passes the two asset URLs as
   data-attributes so this static JS file can stay Liquid-free.

   The signature: the upright 8 rotates 90deg into the lemniscate.
   ============================================================ */

(function () {
  'use strict';

  // Resolve the self-hosted asset URLs handed to us by the script tag.
  // Fall back to bare filenames only if the attributes are missing (they
  // should always be present in the theme).
  var self = document.currentScript;
  var GSAP_URL = (self && self.getAttribute('data-gsap-url')) || 'gsap.min.js';
  var ST_URL = (self && self.getAttribute('data-scrolltrigger-url')) || 'ScrollTrigger.min.js';

  var reduceMotion =
    window.matchMedia &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  // Mark the document so CSS knows JS-driven motion is coming. Without this
  // class, reveal targets stay visible (the safe default). With it, CSS hides
  // them so JS can animate them in. This guarantees: no JS or reduced motion
  // -> everything is visible immediately.
  function enableMotion() {
    document.documentElement.classList.add('huit-motion-on');
  }

  function loadScript(src) {
    return new Promise(function (resolve, reject) {
      var s = document.createElement('script');
      s.src = src;
      s.async = true;
      s.onload = resolve;
      s.onerror = reject;
      document.head.appendChild(s);
    });
  }

  // Statically resolve the 8 into its final lemniscate reading, no animation.
  // Self-contained: sets transform-box inline so it works without the
  // stylesheet (e.g. reduced-motion path before / regardless of CSS).
  function staticFlip(glyph) {
    glyph.style.transformBox = 'view-box';
    glyph.style.transformOrigin = '50% 50%';
    glyph.style.transform = 'rotate(90deg)';
  }

  // Reduced motion: do not load GSAP at all. Reveal targets are already
  // visible because we never add huit-motion-on. We still flip the 8 into
  // its final infinity reading once, with no animation, so the mark resolves.
  if (reduceMotion) {
    document.querySelectorAll('[data-huit-8-glyph]').forEach(staticFlip);
    return;
  }

  enableMotion();

  var gsap, ScrollTrigger, ctx;

  // Build (or rebuild) the reveal setup. Runs inside a single gsap.context()
  // so every tween / ScrollTrigger it creates can be reverted in one call.
  // Safe to call again after a section re-render: we revert the old context
  // first so nothing leaks.
  function buildMotion() {
    if (ctx) ctx.revert();

    ctx = gsap.context(function () {
      var mm = gsap.matchMedia();

      /* ---- The 8 -> infinity hero rotation (the signature) ----
         One per page. On the homepage hero mark, the upright 8 rotates 90deg
         into the lemniscate on load. Crisp, single move, around dead center. */
      var heroMark = document.querySelector('[data-huit-8-hero] [data-huit-8-glyph]');
      if (heroMark) {
        gsap.set(heroMark, { transformOrigin: '50% 50%', rotation: 0 });
        gsap.to(heroMark, {
          rotation: 90,
          duration: 1.1,
          ease: 'power3.inOut',
          delay: 0.35,
        });
      }

      /* ---- Scroll-reveal cadence ----
         Sections / rows fade + rise on enter. Calm editorial pace, staggered,
         not bouncy. Mobile gets a lighter rise. Batched so a row of cards
         animates together instead of one trigger per element fighting layout. */
      mm.add(
        {
          isDesktop: '(min-width: 750px)',
          isMobile: '(max-width: 749px)',
        },
        function (mctx) {
          var rise = mctx.conditions.isMobile ? 12 : 20;

          var targets = gsap.utils.toArray('[data-huit-reveal]');

          // On the homepage, also reveal product cards (the archive rows)
          // without editing the shared product-list section. Scoped to the
          // index template so PDP / collection grids keep their own behaviour.
          var main = document.getElementById('MainContent');
          var isIndex = main && main.getAttribute('data-template') === 'index';
          if (isIndex) {
            var cards = gsap.utils.toArray('.resource-list__item');
            targets = targets.concat(cards);
          }

          if (!targets.length) return;

          // Establish the hidden start state for every reveal target.
          gsap.set(targets, { opacity: 0, y: rise });

          ScrollTrigger.batch(targets, {
            start: 'top 88%',
            once: true,
            onEnter: function (batch) {
              gsap.to(batch, {
                opacity: 1,
                y: 0,
                duration: 0.7,
                ease: 'power2.out',
                stagger: 0.08,
                overwrite: true,
              });
            },
          });

          // Layout settles after fonts / images; recalc trigger positions.
          ScrollTrigger.refresh();
        }
      );
    });

    // Recalc once everything (fonts, lazy media) has loaded, so reveals and
    // the hero mark sit on accurate positions.
    if (document.readyState === 'complete') {
      ScrollTrigger.refresh();
    } else {
      window.addEventListener('load', function () {
        ScrollTrigger.refresh();
      });
    }
  }

  function init() {
    gsap = window.gsap;
    ScrollTrigger = window.ScrollTrigger;
    if (!gsap || !ScrollTrigger) return;

    gsap.registerPlugin(ScrollTrigger);
    gsap.defaults({ ease: 'power2.out', duration: 0.6 });

    buildMotion();

    /* ---- Shopify theme-editor lifecycle ----
       When the merchant edits in the customizer, Shopify re-renders sections.
       Rebuild the whole reveal setup (reverting the old context first, so no
       ScrollTriggers leak) and immediately force the affected section's reveal
       targets visible so a freshly loaded section never stays blank. */
    document.addEventListener('shopify:section:load', function (event) {
      var section = event.target;
      if (section && section.querySelectorAll) {
        section.querySelectorAll('[data-huit-reveal]').forEach(function (el) {
          gsap.set(el, { opacity: 1, y: 0, clearProps: 'willChange' });
        });
      }
      buildMotion();
    });

    document.addEventListener('shopify:section:unload', function () {
      // Rebuild against the new DOM. buildMotion reverts the prior context
      // first, killing the unloaded section's ScrollTriggers cleanly.
      buildMotion();
    });
  }

  // Load GSAP after the page is interactive so it never competes with LCP.
  function boot() {
    loadScript(GSAP_URL)
      .then(function () {
        return loadScript(ST_URL);
      })
      .then(init)
      .catch(function () {
        // Load failed: ensure nothing stays hidden. Reveal everything.
        document.documentElement.classList.remove('huit-motion-on');
      });
  }

  if (window.requestIdleCallback) {
    window.requestIdleCallback(boot, { timeout: 1500 });
  } else {
    window.addEventListener('load', boot);
  }
})();
